/* tslint:disable object-literal-sort-keys */
import React from 'react';

import { Button } from 'react-bootstrap';

import { Icon } from '../../common';
import { FormattingUtils, TableRenderingUtils } from '../../../utils';
import { IOption } from '../../../stores/OptionsStoreClass';
import { IReactTableColumn, IReactTableItem } from '../../../interfaces';

// The second header row of the loans table carries its totals; here it carries the row count, a
// list of cases having nothing to sum. Resolutions and update sources come back as raw enum values,
// so their columns render the label from the option list rather than the value.
const { renderDate, renderOptionLabel, renderTotalRowCount, renderUnformatted } = TableRenderingUtils
  , { getOrDefault } = FormattingUtils
  ;

// The whole row is the click target -- CasesTable hands the click to the row through getTrProps -- so
// this renders the affordance and nothing else, as the loans table's view column does.
function renderViewCase () {
  return (
    <div>
      <Button bsStyle='link' className='icon-right'>
        View <Icon type='angle-double-right' />
      </Button>
    </div>
  );
}

/**
 * Two header rows, as on the loans and liens tables: the labels sit on the first, and the second
 * carries the row count and the sort controls. That keeps the arrows off the labels, which is what
 * crowds them into an ellipsis on a mid-sized screen.
 *
 * Built per set of options rather than declared as a constant, the resolution column needing the
 * option list the store holds.
 */
export function caseColumns (resolutionOptions: IOption[]): IReactTableColumn[] {
  return [
    // Leftmost, ahead of the plaintiff, as on the loans and liens tables. The cell is right-aligned
    // within its own width, so the link sits snug against the column beside it.
    {
      columns: [{
        className: 'view-case-column',
        Cell: renderViewCase,
        sortable: false,
        width: 90,
      }],
    },
    {
      Header: 'Plaintiff',
      columns: [{
        Header: renderTotalRowCount,
        accessor: 'plaintiff_full_name',
        Cell: renderUnformatted,
        minWidth: 130,
        maxWidth: 220,
        sortable: true,
      }],
    },
    {
      // Court Docket / Index Number abbreviated: it is the widest heading of the five by some way,
      // and the words it drops are the ones the reader supplies anyway
      Header: 'Docket / Index No.',
      columns: [{
        accessor: 'court_id',
        Cell: renderUnformatted,
        className: 'col-style-gray',
        minWidth: 120,
        maxWidth: 200,
        sortable: true,
      }],
    },
    {
      Header: 'Resolution',
      columns: [{
        accessor: 'resolution',
        Cell: renderOptionLabel.bind(null, resolutionOptions),
        minWidth: 90,
        maxWidth: 130,
        sortable: true,
      }],
    },
    // The two sides are dated apart because a case the court has moved on and the firm has said
    // nothing about since is the one worth chasing. 'Last' is left off both: a single date column
    // per side can only be the latest, and the pair has to fit alongside the three columns before it.
    {
      Header: 'Court Update',
      columns: [{
        accessor: 'last_court_records_update_date',
        Cell: renderDate,
        className: 'col-style-gray',
        minWidth: 110,
        maxWidth: 160,
        sortable: true,
      }],
    },
    {
      Header: 'Firm Update',
      columns: [{
        accessor: 'last_law_firm_update_date',
        Cell: renderDate,
        className: 'col-style-gray',
        minWidth: 110,
        maxWidth: 160,
        sortable: true,
      }],
    },
  ];
}

// Newest first, both sources in the one list: what happened to the case is the order that matters,
// and which side reported a given entry is the column beside it. The API orders them; the table
// keeps that order rather than sorting again.
export function caseUpdateColumns (sourceOptions: IOption[]): IReactTableColumn[] {
  return [
    {
      accessor: 'update_date',
      Cell: renderDate,
      Header: 'Date',
      maxWidth: 120,
      minWidth: 90,
    },
    {
      accessor: 'source',
      Cell: renderOptionLabel.bind(null, sourceOptions),
      Header: 'Source',
      maxWidth: 140,
      minWidth: 100,
    },
    {
      accessor: 'body',
      // Not renderUnformatted, which ellipsises to one line: an update is a paragraph, and the
      // column is what the modal is open to read
      Cell: (item: IReactTableItem) => <div className='update-body'>{getOrDefault(item.value)}</div>,
      Header: 'Update',
      minWidth: 200,
    },
  ];
}
